import Cookies from 'js-cookie'
import { handleSessionExpired } from '@/lib/authToken'
import type { ListMailboxesResponse } from '../types'

const API_BASE = (import.meta.env?.VITE_API_URL as string) || ''

function buildUrl(path: string): string {
  if (API_BASE) return `${API_BASE.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
  return path.startsWith('/') ? path : `/${path}`
}

async function apiFetch<T>(method: string, path: string): Promise<T> {
  const token = Cookies.get('token')
  const headers: Record<string, string> = {}
  if (token) headers.Authorization = `Bearer ${token}`
  const res = await fetch(buildUrl(path), { method, headers })
  if (!res.ok) {
    if (res.status === 401 && token) {
      handleSessionExpired()
      throw new Error('Session expired')
    }
    const payload = (await res.json().catch(() => null)) as { error?: { code?: string; message?: string } } | null
    const err = new Error(payload?.error?.message || res.statusText || 'Request failed')
    ;(err as Error & { code?: string }).code = payload?.error?.code
    throw err
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export async function listMailboxes(workspaceId: string): Promise<ListMailboxesResponse> {
  return apiFetch<ListMailboxesResponse>('GET', `/api/workspaces/${workspaceId}/mailboxes`)
}

export async function deleteMailbox(workspaceId: string, mailboxId: string): Promise<void> {
  await apiFetch<void>('DELETE', `/api/workspaces/${workspaceId}/mailboxes/${mailboxId}`)
}

// Returns the Google consent URL for a secondary Gmail connect. The gmail
// standalone OAuth route requires JWT auth, so the token goes through here
// (not through a bare `window.location =` on the start URL).
export async function getGmailSecondaryOAuthUrl(workspaceId: string): Promise<string> {
  const data = await apiFetch<{ url: string }>(
    'GET',
    `/api/workspaces/${workspaceId}/gmail/oauth/start?role=secondary&source=campaigns`,
  )
  return data.url
}

// Outlook standalone OAuth start is state-signed (no JWT) and takes a userId
// query param — it redirects to Microsoft directly from the browser.
export function buildOutlookSecondaryStartUrl(workspaceId: string, userId: string): string {
  return buildUrl(
    `/api/workspaces/${workspaceId}/outlook/oauth/start?userId=${encodeURIComponent(userId)}&role=secondary&source=campaigns`,
  )
}
